import { useState, useMemo, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import {
  FiCalendar,
  FiFileText,
  FiLayers,
  FiSend,
  FiArrowRight,
  FiInfo,
} from 'react-icons/fi';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import styles from './Leave.module.scss';
import { Dropdown } from '@/components/Dropdown';
import {
  leaveTypeOptions,
  paidLeaveTypeList,
  paidLeaveTypeOptions,
} from './Leave.constants';
import { TextArea } from '@/components/TextArea';
import { InitialsBadge } from '@/components/InitialsBadge';
import {
  IconBadge,
  iconBadgeSizes,
  iconBadgeShapes,
} from '@/components/IconBadge';
import { InfoBox } from '@/components/InfoBox';
import { borderedSelectStyles } from '@/components/Dropdown/Dropdown.constants';
import { Button, buttonVariants } from '@/components/Button';
import { leaveBalanceService } from '@services/leaveBalance';
import { useSelector } from 'react-redux';
import { addNotification, notificationStates } from '@components/Notification';
import firebase from '@config/firebase.config';
import {
  validateLeaveForm,
  toDateString,
  fromDateString,
} from './Leave.helper';
import { LeaveDatePicker } from './LeaveDatePicker.component';

const database = firebase.firestore();
const COMMENT_MAX_LENGTH = 250;

function formatManagerOptionLabel(option) {
  const label = option.userName || option.label;
  return (
    <div className={styles.personCell}>
      <InitialsBadge label={label} twoLetter />
      <div className={styles.personCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {label}
        </Text>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          {option.role || '—'}
        </Text>
      </div>
    </div>
  );
}

export const LeaveFormModal = ({
  isVisible,
  companyManagers,
  onClose,
  fetchLeaveRequestsList,
  fetchLeaveBalance,
  leaveData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { profile } = useSelector((state) => state.auth);
  const teams = useSelector((state) => ({
    projects: state.team.projects,
    active: state.team.active,
    teams: state?.team?.teams,
  }));
  const [formData, setFormData] = useState({
    leaveType: null,
    leaveComment: '',
    leaveManager: null,
    leaveDate: null,
    // UI-only for now — captured but not sent in the create payload until
    // multi-day leave requests are actually supported end-to-end.
    toDate: null,
    paidLeaveType: null,
  });
  useEffect(() => {
    let defaultLeaveOption = leaveTypeOptions[1];
    if (leaveData?.availableLeaves > 0) {
      defaultLeaveOption = leaveTypeOptions[0];
    }
    setFormData((prev) => ({ ...prev, leaveType: defaultLeaveOption }));
  }, [leaveData]);

  const companyManagersDropdownOptions = useMemo(() => {
    return companyManagers
      .map((manager) => ({
        ...manager,
        label: manager.userName,
        value: manager.userUID,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [companyManagers]);

  // Default to the first manager only while nothing has been picked yet.
  useEffect(() => {
    if (!companyManagersDropdownOptions.length) return;
    setFormData((prev) => {
      if (prev.leaveManager?.value) return prev;
      return { ...prev, leaveManager: companyManagersDropdownOptions[0] };
    });
  }, [companyManagersDropdownOptions]);

  const selectedLeaveDate = useMemo(() => {
    const ld = formData.leaveDate;
    if (!ld || ld.year == null || ld.month == null || ld.day == null) {
      return null;
    }
    const y = Number(ld.year);
    const m = Number(ld.month);
    const d = Number(ld.day);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return null;
    }
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [formData.leaveDate]);

  const selectedToDate = useMemo(() => {
    const td = formData.toDate;
    if (!td || td.year == null || td.month == null || td.day == null) {
      return null;
    }
    const date = new Date(td.year, td.month - 1, td.day);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [formData.toDate]);

  const validatePaidLeaveBalance = async () => {
    setIsSubmitting(true);
    if (paidLeaveTypeList.includes(formData.paidLeaveType?.value)) {
      try {
        // First check if leave policy exists
        const policy = await leaveBalanceService.getLeavePolicy(
          activeCompany?.companyId
        );

        if (!policy) {
          addNotification({
            message:
              'No paid leave policy configured. Please contact your administrator.',
            level: notificationStates.error,
          });
          return false;
        }

        // Get user's effective remaining balance (including pending requests)
        const balance =
          await leaveBalanceService.getEffectiveRemainingPaidLeave(
            profile.id,
            activeCompany?.companyId
          );

        if (!balance.hasPolicy) {
          addNotification({
            message:
              'No paid leave policy configured. Please contact your administrator.',
            level: notificationStates.error,
          });
          return false;
        }

        if (balance.effectiveRemaining <= 0) {
          addNotification({
            message:
              'You have consumed all your available paid leave days. No leave balance remaining.',
            level: notificationStates.error,
          });
          return false;
        }

        // Show informative message about pending requests
        if (balance.pendingRequests > 0) {
          addNotification({
            message: `You have ${balance.effectiveRemaining} paid leave days available for new requests (${balance.totalAllocated} total allocated - ${balance.pendingRequests} pending requests).`,
            level: notificationStates.info,
          });
        }

        return true;
      } catch (error) {
        console.error('Error checking paid leave balance:', error);
        addNotification({
          message: 'Error checking paid leave balance. Please try again.',
          level: notificationStates.error,
        });
        return false;
      } finally {
        setIsSubmitting(false);
      }
    }
    return true;
  };
  const initializeUserLeaveUsageIfNeeded = async () => {
    if (paidLeaveTypeList.includes(formData.leaveType?.value)) {
      try {
        await leaveBalanceService.initializeUserLeaveUsage(
          profile.id,
          activeCompany?.companyId
        );
      } catch (error) {
        console.error('Error initializing user leave usage:', error);
        // Don't block the application if this fails
      }
    }
  };

  const validateToDate = () => {
    if (!selectedToDate) {
      addNotification({
        message: 'Please choose a To Date',
        level: notificationStates.error,
      });
      return false;
    }
    if (selectedLeaveDate && selectedToDate < selectedLeaveDate) {
      addNotification({
        message: 'To Date cannot be before From Date',
        level: notificationStates.error,
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateLeaveForm(formData) || !validateToDate()) {
      setIsSubmitting(false);
      return;
    }
    // Check paid leave balance before submission
    if (!(await validatePaidLeaveBalance())) {
      setIsSubmitting(false);
      return;
    }
    // Initialize user's leave usage document if needed
    await initializeUserLeaveUsageIfNeeded();

    let leaveDate = `${formData.leaveDate.year}-${formData.leaveDate.month}-${formData.leaveDate.day}`;

    const companyLeaveRef = database
      .collection('companyDatabase')
      .doc(activeCompany?.companyId)
      .collection('leaveRequests');
    const query = companyLeaveRef
      .where('userId', '==', profile.id)
      .where('leaveDate', '==', leaveDate)
      .where('status', '!=', 'Declined');
    query.get().then((querySnap) => {
      if (!querySnap.empty) {
        addNotification({
          message: `You already have submitted a request for leave on the date - ${leaveDate}`,
          level: notificationStates.error,
        });
        setIsSubmitting(false);
        return;
      }
      const leaveType =
        formData.leaveType.value === 'Paid Leave'
          ? formData.paidLeaveType?.value
          : formData.leaveType?.value;
      const managerId =
        formData.leaveManager !== ''
          ? formData.leaveManager.userUID
          : companyManagers[0]?.userUID;
      const finalData = {
        userId: profile.id,
        companyId: activeCompany.companyId,
        companyID: activeCompany.companyId,
        teamId: teams.active?.activeTeamid || '',
        assignee: managerId,
        managerId,
        status: 'Pending',
        type: leaveType || '',
        comment: formData.leaveComment || 'No Comment',
        leaveDate,
        requestDate: new Date(),
      };
      return companyLeaveRef
        .add(finalData)
        .then(() => {
          addNotification({
            message: 'Request submitted successfully',
            level: notificationStates.success,
          });
          setFormData({
            leaveType: null,
            leaveComment: '',
            leaveManager: null,
            leaveDate: null,
            toDate: null,
          });
          fetchLeaveBalance();
          setIsSubmitting(false);
          onClose();
          fetchLeaveRequestsList();
        })
        .catch((error) => {
          addNotification({
            message: 'Request submission failed',
            level: notificationStates.error,
          });
          console.log(error);
          setIsSubmitting(false);
        });
    });
  };
  const filteredLeaveTypeOptions = useMemo(() => {
    let filteredOptions = [];
    if (leaveData?.availableLeaves === 0) {
      filteredOptions = leaveTypeOptions.filter(
        (option) => option.value !== leaveTypeOptions[0].label
      );
    } else {
      filteredOptions = leaveTypeOptions.filter(
        (option) => option.value !== leaveTypeOptions[1].label
      );
    }
    return filteredOptions;
  }, [leaveData]);

  return (
    <Dialog
      visible={isVisible}
      onHide={onClose}
      style={{ width: '640px' }}
      header={
        <div className={styles.leaveDialogHeader}>
          <IconBadge
            icon={<FiCalendar size={20} color="var(--indigo-600)" />}
            bg="indigo-100"
            size={iconBadgeSizes.lg}
            shape={iconBadgeShapes.circle}
          />
          <div className={styles.leaveDialogHeaderText}>
            <Text
              variant={textVariantsEnum.Text_lg}
              fontWeight={fontWeightEnum.Semibold}
            >
              Add Leave Request
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Normal}
              color="gray-600"
            >
              Fill in the details below to request a leave.
            </Text>
          </div>
        </div>
      }
      footer={
        <div className={styles.leaveDialogFooter}>
          <Button
            variant={buttonVariants.secondary}
            text="Cancel"
            onClick={onClose}
            disabled={isSubmitting}
          />
          <Button
            variant={buttonVariants.primary}
            text="Submit Request"
            icon={<FiSend size={16} />}
            onClick={() => {
              setIsSubmitting(true);
              handleSubmit();
            }}
            loading={isSubmitting}
          />
        </div>
      }
    >
      <div className={styles.leaveFormModalContent}>
        <div className={styles.formRow2Col}>
          <Dropdown
            options={filteredLeaveTypeOptions}
            label="Leave Type *"
            value={formData.leaveType}
            selectStyles={borderedSelectStyles}
            leftIcon={<FiFileText size={14} color="var(--indigo-500)" />}
            onChange={(option) =>
              setFormData((prev) => ({ ...prev, leaveType: option }))
            }
          />
          {formData.leaveType?.value === 'Paid Leave' && (
            <Dropdown
              options={paidLeaveTypeOptions}
              label="Paid Leave Type*"
              value={formData.paidLeaveType}
              selectStyles={borderedSelectStyles}
              leftIcon={<FiLayers size={14} color="var(--indigo-500)" />}
              placeholder="Select leave type"
              onChange={(option) =>
                setFormData((prev) => ({ ...prev, paidLeaveType: option }))
              }
            />
          )}
        </div>

        <div className={styles.dateRow}>
          <div className={styles.datePicker}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.label}
            >
              From Date *
            </Text>
            <LeaveDatePicker
              compact
              date={toDateString(formData.leaveDate)}
              onDateChange={(dateString) =>
                setFormData((prev) => ({
                  ...prev,
                  leaveDate: fromDateString(dateString),
                }))
              }
              placeholder="Select date"
            />
          </div>
          <span className={styles.dateRowArrow}>
            <FiArrowRight size={14} />
          </span>
          <div className={styles.datePicker}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.label}
            >
              To Date *
            </Text>
            <LeaveDatePicker
              compact
              date={toDateString(formData.toDate)}
              onDateChange={(dateString) =>
                setFormData((prev) => ({
                  ...prev,
                  toDate: fromDateString(dateString),
                }))
              }
              placeholder="Select date"
            />
          </div>
        </div>

        <TextArea
          label="Comment (Optional)"
          placeholder="Add a comment..."
          value={formData.leaveComment}
          maxLength={COMMENT_MAX_LENGTH}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, leaveComment: e.target.value }))
          }
        />

        <Dropdown
          label="Manager *"
          options={companyManagersDropdownOptions}
          value={formData.leaveManager}
          selectStyles={borderedSelectStyles}
          formatOptionLabel={formatManagerOptionLabel}
          onChange={(option) =>
            setFormData((prev) => ({ ...prev, leaveManager: option }))
          }
        />

        <InfoBox icon={<FiInfo size={16} />}>
          Your leave request will be sent to your manager for approval.
        </InfoBox>
      </div>
    </Dialog>
  );
};
